import { Descriptions, Select, Table, TableColumnsType } from 'antd';
import _ from 'lodash';
import { Children, FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import useAxios from '../../../hooks/useAxios';
import { RootState } from '../../../store/rootReducer';
import ErrorResponse from '../../ErrorResponse';
import Loader from '../../Loader';
import { IElearningItem, IElearningItemResultCount, IGetElearning } from '../../../models/eLearning.model';
import { getClassElearning } from '../../../api/campusClasses.api';
import moment from 'moment';
import styled from 'styled-components';
import ClassElearningBadWord from '../class/modals/ClassElearningBadWord';

const { Option } = Select;

const Block = styled.div`
  .table-custom {
    border-top: 0px;
  }
`;

interface IProps {
  classCode: string;
  semesterCode: string;
}

const ClassElearning: FC<IProps> = ({
  classCode,
  semesterCode
}) => {

  const CRITERIA_ELEARNING_STATUS = 80;
  const DISPLAY_PERCENT_DECIMAL = 1;

  const { t, i18n } = useTranslation();
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  const { chosenClass } = useSelector((state: RootState) => state.class);
  const { data: res, isLoading, error, execute } = useAxios<IGetElearning>();

  const [data, setData] = useState<IElearningItem[]>();
  const [resultCount, setResultCount] = useState<IElearningItemResultCount>();
  const [monthSeq, setMonthSeq] = useState(0);
  const [modalParams, setModalParams] = useState<{ classCode: string; semesterCode: string; memberCode: string } | undefined>(undefined);
  const [isModalVisible, setIsModalVisible] = useState(false);
  
  const SY0016 = commonCodes.find(item => item.commonCode === 'SY0016')?.detailCode;

  


  const getElearningData = () => {
    execute( monthSeq && monthSeq !== 0 
      ? getClassElearning({classCode, semesterCode, monthSeq})
      : getClassElearning({classCode, semesterCode})
    );
  }

  const parsePercent = (targetCount: number, allCount: number) => (targetCount === 0 && allCount === 0) ? 0 : targetCount / allCount * 100;
  const getMemberName = (item: IElearningItem) => i18n.language === 'ko' ? item.memberKorName : item.memberEngName

  const sortPercent = (prevItem: IElearningItem, nextItem: IElearningItem) => {
    return parsePercent(prevItem.totalCompleteCount, prevItem.totalCount) - parsePercent(nextItem.totalCompleteCount, nextItem.totalCount);
  }

  const sortMemberName = (prevItem: IElearningItem, nextItem: IElearningItem) => {
    const prevMemberName = getMemberName(prevItem);
    const nextMemberName = getMemberName(nextItem);
    return prevMemberName < nextMemberName ? -1 : prevMemberName > nextMemberName ? 1 : 0;
  }

  const checkZeroCount = (targetCount: number, allCount: number) => allCount === 0 && targetCount === 0;

  const getTextColor = (completeCount: number, allCount: number) => {
    const isZeroCount = checkZeroCount(completeCount, allCount);
    if(isZeroCount) {
      return '';
    }
    return `c-${parsePercent(completeCount, allCount) >= CRITERIA_ELEARNING_STATUS ? 'blue' : 'pink'}`;
  }

  const displayPercent = (targetCount: number, allCount: number) => {
    const percent = parsePercent(targetCount, allCount);
    const isZeroCount = checkZeroCount(targetCount, allCount);

    if(isZeroCount) {
      return '-';
    } else if (percent === 0) {
      return '0%';
    } else {
      return percent.toFixed(DISPLAY_PERCENT_DECIMAL) + '%';
    }
  }
  

  useEffect(() => {
    getElearningData();
  }, []);

  useEffect(() => {
    if (res && !isLoading) {
      const data = _.cloneDeep(res.data)
        .sort((a: IElearningItem, b: IElearningItem)=> sortPercent(a, b) || sortMemberName(a, b));

      setData(data);
    }
  }, [res, isLoading]);

  useEffect(() => {
    const result = {
      complete: {
        icoaching: data?.reduce((acc, curr)=> (acc + curr.icoachingCompleteCount), 0) || 0,
        elearning: data?.reduce((acc, curr)=> (acc + curr.elearningCompleteCount), 0) || 0,
        clinic: data?.reduce((acc, curr)=> (acc + curr.clinicCompleteCount), 0) || 0,
      },
      all: {
        icoaching: data?.reduce((acc, curr)=> (acc + curr.icoachingCount), 0) || 0,
        elearning: data?.reduce((acc, curr)=> (acc + curr.elearningCount), 0) || 0,
        clinic: data?.reduce((acc, curr)=> (acc + curr.clinicCount), 0) || 0,
      }
    }
    setResultCount(result);
  }, [data]);
  


  useEffect(()=> {
    getElearningData();
  }, [monthSeq])

  return (
    <>
      {isLoading ? <Loader /> : null}
      <ErrorResponse error={error} />

      <div className="comm-bx__between"> 
        <h3 className='sub-title'>{chosenClass?.className}</h3>
      </div>

      <div className="comm-table__area rowType">
        <Descriptions
          bordered
          column={4}
          size="small"
          labelStyle={{ width: '13%' }}
          contentStyle={{ width: '13%' }}
        >
          <Descriptions.Item label={t('class.class.column52')}>
            {chosenClass?.courseDetailName}
          </Descriptions.Item>
          <Descriptions.Item label={t('class.class.column86')}>
            {chosenClass?.courseName}
          </Descriptions.Item>
          <Descriptions.Item label={t('class.class.column13')}>
            {chosenClass?.courseSubName}
          </Descriptions.Item>
          <Descriptions.Item label={t('class.class.column42')}>
            {moment().format('YYYY-MM-DD')}
          </Descriptions.Item>
        </Descriptions>
        <div className="comm-bx__space">
          <div className="comm-btn__area right">
            <div className="label">
              Status : <i className="ic-status blue"></i> 수행율 80% 이상 / <i className="ic-status red"></i> 수행율 80% 미만
            </div>
            
            
            <Select
              className='w-150p'
              onChange={setMonthSeq}
              placeholder={t('class.class.placeholder7')}
              value={monthSeq}
            >
              <Option key={0} value={0}>{t('class.class.placeholder7')}</Option>
              { SY0016?.map((code)=> 
                  <Option
                    key={code.detailCode}
                    value={code.detailCode}
                  >
                    {code.korName}
                  </Option>
              )}
            </Select>
          </div>
        </div>
      </div>
        
      <Block className="comm-table__area mt-0">
        { data && resultCount && data.length > 0 && 
          <table className="table-custom">
            <colgroup>
              <col width="6%" />
              <col width="13%" />
              <col width="9%" />
              <col width="9%" />
              <col width="9%" />
              <col width="9%" />
              <col width="9%" />
              <col width="9%" />
              <col width="9%" />
              <col width="9%" />
              <col width="9%" />
            </colgroup>
              <thead>
                <tr>
                  <th className='text-center' rowSpan={3}>No</th>
                  <th className='text-center' rowSpan={3}>Name</th>
                  <th className='text-center' colSpan={3}>Total</th>
                  <th className='text-center' colSpan={2}>iCoaching</th>
                  <th className='text-center' colSpan={2}>e-Learning</th>
                  <th className='text-center' colSpan={2}>Clinic</th>
                </tr>
                <tr>
                  <th className='text-center'>Status</th>
                  <th className='text-center'>Result</th>
                  <th className='text-center'>%</th>
                  <th className='text-center'>Result</th>
                  <th className='text-center'>%</th>
                  <th className='text-center'>Result</th>
                  <th className='text-center'>%</th>
                  <th className='text-center'>Result</th>
                  <th className='text-center'>%</th>
                </tr>
                <tr>
                  <th>
                    -
                  </th>

                  <th className='text-center'>
                    <span>
                      { `${Math.max(...data?.map(item=> item.icoachingCount)) + Math.max(...data?.map(item=> item.elearningCount))} + α` }
                    </span>
                  </th>

                  
                  <th className='text-center'>-</th>
                  

                  <th className='text-center'>
                    { Math.max(...data?.map(item=> item.icoachingCount)) }
                  </th>

                  <th className='text-center'>-</th>

                  <th className='text-center'>
                    { Math.max(...data?.map(item=> item.elearningCount)) }
                  </th>

                  <th className='text-center'>-</th>

                  <th className='text-center'>
                    <span>α</span>
                  </th>

                  <th className='text-center'>-</th>
                </tr>
                
              </thead>
              <tbody>
                  { data?.map((item, index)=> (
                    <tr >
                      <td className='text-center'>{index + 1}</td>
                      <td>{item.memberKorName} <span>({item.memberEngName})</span></td>
                      <td className='text-center'>
                        <i className={`ic-status ${parsePercent(item.totalCompleteCount, item.totalCount) >= CRITERIA_ELEARNING_STATUS ? 'blue' : 'red'}`}/>
                      </td>
                      <td className='text-center'>
                        <span className={getTextColor(item.totalCompleteCount, item.totalCount)}>{item.totalCompleteCount}</span>/{item.totalCount}
                      </td>
                      <td className='text-center'>{ displayPercent(item.totalCompleteCount, item.totalCount) }</td>
                      <td className='text-center'>
                        <span className={`${getTextColor(item.icoachingCompleteCount, item.icoachingCount)}`}>{ item.icoachingCompleteCount}</span>/{item.icoachingCount }
                      </td>
                      <td className='text-center'>{ displayPercent(item.icoachingCompleteCount, item.icoachingCount) }</td>
                      <td className='text-center'>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {/* e-Learning Result 셀 */}
                          <span className={`${getTextColor(item.elearningCompleteCount, item.elearningCount) }`}>
                            {item.elearningCompleteCount}
                          </span>/{item.elearningCount}
                          {parsePercent(item.elearningCompleteCount, item.elearningCount) < CRITERIA_ELEARNING_STATUS &&
                           !checkZeroCount(item.elearningCompleteCount, item.elearningCount) &&
                           (!!item.profanityCount && item.profanityCount > 0) && (
                            <span style={{ marginLeft: 4, display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}
                              onClick={() => {
                                setModalParams({
                                  classCode: classCode,
                                  semesterCode: semesterCode,
                                  memberCode: item.memberCode || '',
                                });
                                setIsModalVisible(true);
                              }}
                            >
                              <svg width="18" height="16" viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M7.199 1c.77-1.333 2.695-1.333 3.465 0l6.927 12c.746 1.291-.133 2.896-1.588 2.995l-.144.005v-1a1 1 0 0 0 .866-1.5l-6.928-12a1 1 0 0 0-1.732 0l-6.928 12a1 1 0 0 0 .867 1.5v1l-.144-.005C.452 15.9-.417 14.394.204 13.126L.27 13 7.2 1zm8.66 14v1H2.004v-1h13.855z" fill="#BB0665"/>
                                <path d="M8.932 5v4M9 12v1" stroke="#BB0665" stroke-width="2" stroke-linecap="round"/>
                              </svg>
                            </span>
                          )}
                        </div>
                      </td>
                      {/* e-Learning % 셀 */}
                      <td className={
                        `text-center ${getTextColor(item.elearningCompleteCount, item.elearningCount)}`
                      }>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          <span>{displayPercent(item.elearningCompleteCount, item.elearningCount)}</span>
                        </div>
                      </td>
                      <td className='text-center'>
                        <span className={`${getTextColor(item.clinicCompleteCount, item.clinicCount) }`}>{item.clinicCompleteCount}</span>/{item.clinicCount}
                      </td>
                      <td className='text-center'>{ displayPercent(item.clinicCompleteCount, item.clinicCount) }</td>
                    </tr>
                  ))}
                
              </tbody>
          </table>
        }
      </Block>

      {/* ClassElearningBadWord 컴포넌트 추가 */}
      <ClassElearningBadWord
        visible={isModalVisible} // isModalVisible 상태에 따라 가시성 제어
        modalParams={modalParams} // modalParams 상태 전달
        onCancel={() => setIsModalVisible(false)} // 모달 닫기 핸들러 전달
      />
    </>
  );
};

export default ClassElearning;
